import { describeNonConformity, type NonConformity } from '../quality/non-conformity';
import { summarizeVerification, type ServiceVerification } from '../quality/service-verification';
import type { BoardActivity } from './weekly-board';

/**
 * A seção "Verificação de qualidade" do RDO (Fase 19, fatia 4).
 *
 * ```text
 * Verificação de qualidade
 * Frente                        Serviço                    Checklist       Aprov.  Reprov.  Não conformidades
 * Alvenaria — 3º Pavimento      ASSENTAMENTO DE ALVENARIA  FORM.109 …        6       1      nº 48 — Junta com 25 mm
 * Alvenaria — 3º Pavimento      VERGAS / CONTRAVERGAS      sem checklist     —       —
 * ```
 *
 * ## Derivada das verificações, e não das frentes conferidas
 *
 * A seção sai **das verificações do dia** (ADR-128): quem verificou afirmou por si, e a
 * frente continua verificada mesmo que a conferência de quem a executou seja reaberta ou
 * vire ausência depois. Cruzar com a RN-216 apagaria do documento uma NC que continua
 * aberta e numerada — e o documento existe justamente para ela constar.
 *
 * A NC cancelada não sai: é a linha riscada da planilha, e o papel do dia não a lista.
 */

export interface QualityNonConformityLine {
  readonly number: number;
  readonly itemLabel: string;
  readonly description: string;
  /** O que foi feito para corrigir (RN-294). Ausente nas NCs de antes da `0043`. */
  readonly solution?: string;
}

export interface QualityLine {
  readonly activityId: string;
  /** `"Alvenaria — 3º Pavimento"` — o rótulo da frente, ou a disciplina copiada se a frente sumiu. */
  readonly description: string;
  readonly serviceName: string;
  /** As fichas respondidas, pelo nome. Ausente = serviço declarado sem ficha. */
  readonly checklistName?: string;
  readonly approved: number;
  readonly rejected: number;
  readonly notApplicable: number;
  readonly nonConformities: readonly QualityNonConformityLine[];
  readonly verifiedByName: string;
}

export function buildQualityLines(input: {
  readonly verifications: readonly ServiceVerification[];
  readonly nonConformities: readonly NonConformity[];
  readonly activities: ReadonlyMap<string, BoardActivity>;
}): readonly QualityLine[] {
  const ncsPorVerificacao = new Map<string, NonConformity[]>();
  for (const nc of input.nonConformities) {
    if (nc.status === 'CANCELLED') continue;
    const lista = ncsPorVerificacao.get(nc.verificationId) ?? [];
    lista.push(nc);
    ncsPorVerificacao.set(nc.verificationId, lista);
  }

  return input.verifications
    .map((verification): QualityLine => {
      const resumo = summarizeVerification(verification);
      const nomes = verification.checklists.map((item) => item.name).join('; ');
      const ncs = [...(ncsPorVerificacao.get(verification.id) ?? [])].sort(
        (a, b) => a.number - b.number,
      );
      return {
        activityId: verification.activityId,
        description:
          input.activities.get(verification.activityId)?.label ?? verification.disciplineName,
        serviceName: verification.serviceName,
        approved: resumo.approved,
        rejected: resumo.rejected,
        notApplicable: resumo.notApplicable,
        nonConformities: ncs.map((nc) => ({
          number: nc.number,
          itemLabel: nc.itemLabel,
          description: nc.description,
          ...(nc.solution === undefined ? {} : { solution: nc.solution }),
        })),
        verifiedByName: verification.verifiedByName,
        ...(nomes === '' ? {} : { checklistName: nomes }),
      };
    })
    .sort(
      (a, b) =>
        a.description.localeCompare(b.description, 'pt-BR') ||
        a.serviceName.localeCompare(b.serviceName, 'pt-BR'),
    );
}

/**
 * `nº 48 — Junta com 25 mm (solução: refeita); nº 49 — Fora de prumo (solução: —)` — como a
 * coluna sai no papel, tela e PDF. A NC de antes da solução sai com "—" no lugar dela.
 */
export function describeNonConformities(line: Pick<QualityLine, 'nonConformities'>): string {
  return line.nonConformities.map(describeNonConformity).join('; ');
}
